<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { Button, ButtonV2, ToggleV2, Checkbox, Text, Alert } from '@Shelly/shelly-design-system/index';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { real_id } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import {
		getDeviceSettings,
		getDeviceStatus,
		readDeviceSettings,
	} from 'src/stores/device-runtime/status-selectors';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { SettingGen2RebootRequest } from 'src/types/requests/settings/setting_g2_reboot';
	import { copy } from 'src/helpers/copy_text';
	import { onMount, getContext, onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { QRCode as QrCode } from '@kodav.dev/svelte5-qrcode';
	import { rebootRequired } from 'src/stores/device-runtime/states';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';
	import SettingGuide from '../SettingGuide.svelte';

	export let device: Device;
	export let isOffline;

	const id = real_id(device.id);
	$: settingsStore = getDeviceSettings(id);
	$: matter = $settingsStore?.matter;
	let waitForResponse = false;
	let manualCode = '';
	let qrCode = '';

	$: statusStore = getDeviceStatus(id);
	$: commissionable = $statusStore?.matter?.commissionable;

	const getQrCode = async () => {
		const res: SettingRequest = await gen2Request(device, 'Matter.GetSetupCode', {});

		if (res.isok) {
			manualCode = res?.data?.manual_code;
			qrCode = res?.data?.qr_code;
		} else {
			manualCode = '';
			qrCode = '';
		}
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	onMount(() => {
		getQrCode();
	});

	const enableDisableMatter = async () => {
		waitForResponse = true;
		const res: SettingGen2RebootRequest = await gen2Request(device, 'Matter.SetConfig', {
			config: { enable: matter.enable },
		});
		if (res.isok) {
			const settings = { ...readDeviceSettings(id) };
			settings.matter = matter;
			devices_settings.update_devices_settings(id, settings);
		}
		onSettingsResponse(res, device);
		getQrCode();
		waitForResponse = false;
	};

	$: if (stickyBottomAction) {
		if ($showNewLayout && matter?.enabled) {
			stickyBottomAction.set({
				onClick: enableDisableMatter,
				loading: waitForResponse,
				disabled: isOffline,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	const onToggleChange = () => {
		matter.enable = !matter.enable;
		enableDisableMatter();
	};
</script>

<SettingGuide
	text={$format('settings_guide_matter', {
		default:
			'Add this device to a Matter fabric (Apple Home, Google Home, Alexa, and others) so you can control it from those apps.',
	})}
/>

{#if $showNewLayout}
	<ToggleV2
		size="m"
		class="mb-m"
		checked={matter.enable}
		on:click={onToggleChange}
		disabled={isOffline}
		id="checkbox-asdw6sf"
		info={$format('text_description_matter', {
			default:
				'Enabling or disabling Matter will require a reboot in order the change to be applied properly.',
		})}
	>
		{capFirstChar($format('sub_settings_title_matter', { default: 'matter' }))}
	</ToggleV2>
	{#if matter?.enable}
		{#if commissionable}
			{#if manualCode}
				<Alert
					class="mb-m"
					heading={`<strong>${$format('manual_code', {
						default: 'Manual code:',
					})}</strong> ${manualCode}`}
				>
					<svelte:fragment slot="bottom">
						<ButtonV2
							size="m"
							on:click={() =>
								copy(
									manualCode,
									$format('copied_successfully', {
										default: 'Copied successfully!',
									}),
								)}>{$format('button_copy_code')}</ButtonV2
						>
					</svelte:fragment>
				</Alert>
			{/if}

			{#if qrCode}
				<Text styleType="body-small" class="mb-xs text-center">
					{$format('qr_code', {
						default: 'QR code',
					})}
				</Text>
				<div class="mb-m d-flex justify-content-center">
					<QrCode data={qrCode} backgroundColor="#242527" color="#fff" />
				</div>
			{/if}
		{:else}
			<Alert
				class="mb-m"
				heading={$format('text_commission_expired', {
					default:
						'Commissioning window is expired. Reboot the device if you want to add it in Matter fabric.',
				})}
			/>

			<ButtonV2
				size="m"
				class="mb-s"
				width="full"
				disabled={isOffline}
				variant="secondary"
				on:click={() => rebootRequired.set(device)}
			>
				{$format('button_reboot_device', { default: 'Reboot device' })}
			</ButtonV2>
		{/if}
	{/if}
{:else}
	<Checkbox
		size="l"
		class="mb-s"
		bind:checked={matter.enable}
		disabled={isOffline}
		id="checkbox-asdw6sf"
	>
		{capFirstChar($format('option_enable', { default: 'enable' }))}
	</Checkbox>

	{#if manualCode || qrCode}
		<hr class="mb-m" />

		{#if commissionable}
			{#if manualCode}
				<Text styleType="body-small" class="mb-m">
					{$format('manual_code', {
						default: 'Manual code:',
					})}
					<span
						role="presentation"
						class="link-styles text-small"
						on:click={() =>
							copy(
								manualCode,
								$format('copied_successfully', {
									default: 'Copied successfully!',
								}),
							)}
					>
						{manualCode}
					</span>
				</Text>
			{/if}

			{#if qrCode}
				<Text styleType="body-small" class="mb-xs text-center">
					{$format('qr_code', {
						default: 'QR code',
					})}
				</Text>
				<div class="mb-m d-flex justify-content-center">
					<QrCode data={qrCode} backgroundColor="#242527" color="#fff" />
				</div>
			{/if}
		{:else}
			<Text styleType="body-small" class="mb-s">
				{$format('text_commission_expired', {
					default:
						'Commissioning window is expired. Reboot the device if you want to add it in Matter fabric.',
				})}
			</Text>

			<Button
				size="m"
				class="mb-s"
				width="full"
				disabled={isOffline}
				variant="secondary"
				on:click={() => rebootRequired.set(device)}
			>
				{$format('button_reboot_device', { default: 'Reboot device' })}
			</Button>
		{/if}
	{/if}

	<Button
		size="m"
		width="full"
		on:click={enableDisableMatter}
		loading={waitForResponse}
		disabled={isOffline}
	>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}
